import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import '../styles/Dashboard.css';
import Header from '../components/Header';
import tuyenDungService from '../services/tuyenDungService';
import dotTuyenDungService from '../services/dotTuyenDungService';
import danhMucService from '../services/danhMucService';
import { transformApplicants, transformDotTuyenDungs } from '../utils/dataTransformers';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const displayName = user?.hoVaTen || user?.name || user?.username || '';
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recruitmentCampaigns, setRecruitmentCampaigns] = useState([]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const maSo = user?.maSo || user?.username;
      const results = await Promise.allSettled([
        dotTuyenDungService.getAll(),
        tuyenDungService.getAll(),
        danhMucService.getTonGiao(),
        danhMucService.getTinhTrangHonNhan(),
        danhMucService.getTrangThaiTuyenDung(),
        danhMucService.getXepLoaiDaoTao(),

        maSo ? tuyenDungService.getDotTuyenDungTheoMaSo(maSo) : Promise.resolve([])
      ]);

      const [dotsRes, applicantsRes, tonGiaosRes, tinhTrangHonNhansRes, trangThaiTuyenDungsRes, xepLoaiDaoTaosRes, allowedDotsRes] = results;

      if (dotsRes.status !== 'fulfilled' || applicantsRes.status !== 'fulfilled') {
        throw dotsRes.reason || applicantsRes.reason;
      }

      const tonGiaos = tonGiaosRes.status === 'fulfilled' ? tonGiaosRes.value : [];
      const tinhTrangHonNhans = tinhTrangHonNhansRes.status === 'fulfilled' ? tinhTrangHonNhansRes.value : [];
      const trangThaiTuyenDungs = trangThaiTuyenDungsRes.status === 'fulfilled' ? trangThaiTuyenDungsRes.value : [];
      const xepLoaiDaoTaos = xepLoaiDaoTaosRes.status === 'fulfilled' ? xepLoaiDaoTaosRes.value : [];
      const allowedDotsValue = allowedDotsRes.status === 'fulfilled' ? allowedDotsRes.value : [];
      const allowedDotIds = Array.isArray(allowedDotsValue) ? allowedDotsValue : (Array.isArray(allowedDotsValue?.data) ? allowedDotsValue.data : []);

      if (tonGiaosRes.status === 'rejected' || tinhTrangHonNhansRes.status === 'rejected' || trangThaiTuyenDungsRes.status === 'rejected' || xepLoaiDaoTaosRes.status === 'rejected') {
        console.warn('Lookup data fetch failed, using empty lists.');
      }

      const dotsData = dotsRes.value;
      const applicantsData = applicantsRes.value;

      const lookupData = { tonGiaos, tinhTrangHonNhans, trangThaiTuyenDungs, xepLoaiDaoTaos };
      const transformedApplicants = transformApplicants(applicantsData, lookupData);
      const transformedCampaigns = transformDotTuyenDungs(dotsData, transformedApplicants);

     
      const filteredCampaigns = transformedCampaigns.filter(campaign =>
        allowedDotIds.includes(campaign.id)
      );

      setRecruitmentCampaigns(filteredCampaigns);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Không thể tải dữ liệu. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  };

  const sortByDate = (list) =>
    [...list].sort((a, b) => (b.id || 0) - (a.id || 0));

  const filteredCampaigns = activeTab === 'all'
    ? sortByDate(recruitmentCampaigns.filter(c => c.status === 'active'))
    : sortByDate(recruitmentCampaigns.filter(c => c.status === activeTab));

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <span className="badge badge-success">Đang diễn ra</span>;
    }
    return <span className="badge badge-secondary">Đã kết thúc</span>;
  };


  const totalStats = {
    positions: recruitmentCampaigns.reduce((sum, c) => sum + c.positions, 0),
    applications: recruitmentCampaigns.reduce((sum, c) => sum + c.applications, 0),
    interviewed: recruitmentCampaigns.reduce((sum, c) => sum + c.interviewed, 0),
    hired: recruitmentCampaigns.reduce((sum, c) => sum + c.hired, 0)
  };

  if (loading) {
    return (
      <div className="dashboard-wrapper">
        <div className="dashboard">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <p>Đang tải dữ liệu...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-wrapper">
        <div className="dashboard">
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: '1rem' }}>
            <p style={{ color: '#dc2626' }}>{error}</p>
            <button onClick={fetchData} className="btn btn-primary">Thử lại</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">

      <div className="app-background">
        <div className="floating-icons">
          <div className="floating-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/>
            </svg>
          </div>
          <div className="floating-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 6h-4V4c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM10 4h4v2h-4V4zm6 11h-3v3h-2v-3H8v-2h3v-3h2v3h3v2z"/>
            </svg>
          </div>
          <div className="floating-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div className="floating-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 9h-2v2H9v-2H7v-2h2V7h2v2h2v2zm0-4V3.5L17.5 9H13z"/>
            </svg>
          </div>
        </div>
      </div>

      <div className="dashboard">

        <Header title="Hệ thống Quản lý Tuyển dụng" />

        <main className="dashboard-main">

          <section className="welcome-section">
            <div className="welcome-card">
              <div className="welcome-content">
                <h2>Xin chào, {displayName}!</h2>
                <p>Chào mừng bạn đến với hệ thống quản lý tuyển dụng. Hãy bắt đầu ngày làm việc hiệu quả!</p>
              </div>
           
            </div>
          </section>


          <section className="campaigns-section">
            <div className="section-header">
              <h3>Đợt tuyển dụng</h3>
              <div className="tab-filters">
                <button
                  className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveTab('all')}
                >
                  Tất cả
                </button>
                <button
                  className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
                  onClick={() => setActiveTab('active')}
                >
                  Đang diễn ra
                </button>
                <button
                  className={`tab-btn ${activeTab === 'completed' ? 'active' : ''}`}
                  onClick={() => setActiveTab('completed')}
                >
                  Đã kết thúc
                </button>
              </div>
            </div>

            <div className="campaigns-grid">
              {filteredCampaigns.map(campaign => (
                <div key={campaign.id} className="campaign-card">
                  <div className="campaign-header">
                    <h4>{campaign.title}</h4>
                    {getStatusBadge(campaign.status)}
                  </div>
                  <div className="campaign-dates">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <span>{campaign.startDate} - {campaign.endDate}</span>
                  </div>
                  <div className="campaign-stats">
                    <div className="campaign-stat">
                      <span className="stat-value">{campaign.positions}</span>
                      <span className="stat-label">Vị trí</span>
                    </div>
                    <div className="campaign-stat">
                      <span className="stat-value">{campaign.applications}</span>
                      <span className="stat-label">Hồ sơ</span>
                    </div>
                    <div className="campaign-stat">
                      <span className="stat-value">{campaign.interviewed}</span>
                      <span className="stat-label">Phỏng vấn</span>
                    </div>
                    <div className="campaign-stat highlight">
                      <span className="stat-value">{campaign.hired}</span>
                      <span className="stat-label">Đạt</span>
                    </div>
                  </div>
                  <div className="campaign-actions">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => navigate(`/campaign/${campaign.id}`)}
                    >
                      Xem chi tiết
                    </button>
                    {campaign.status === 'active' && (
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => navigate(`/campaign/${campaign.id}`)}
                      >
                        Quản lý
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>


          <section className="stats-section">
            <div className="section-header">
              <h3>Tổng quan</h3>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon blue">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                </div>
                <div className="stat-info">
                  <span className="stat-number">{totalStats.positions}</span>
                  <span className="stat-label">Vị trí tuyển dụng</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon green">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="8.5" cy="7" r="4" />
                    <line x1="20" y1="8" x2="20" y2="14" />
                    <line x1="23" y1="11" x2="17" y2="11" />
                  </svg>
                </div>
                <div className="stat-info">
                  <span className="stat-number">{totalStats.applications}</span>
                  <span className="stat-label">Hồ sơ ứng tuyển</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon orange">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </div>
                <div className="stat-info">
                  <span className="stat-number">{totalStats.interviewed}</span>
                  <span className="stat-label">Đã phỏng vấn</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon purple">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <div className="stat-info">
                  <span className="stat-number">{totalStats.hired}</span>
                  <span className="stat-label">Đạt</span>
                </div>
              </div>
            </div>
          </section>
        </main>

        <footer className="dashboard-footer">
          <p>&copy; Copyright 2026 {process.env.REACT_APP_FOOTER_NAME || 'Bệnh Viện Hùng Vương Gia Lai'}</p>
          <p>Powered by Ban Chuyển Đổi Số - leanhquan™</p>
        </footer>
      </div>
    </div>
  );
};

export default Dashboard;

